'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, usePathname } from 'next/navigation';
import type { ShowDetailRecord } from '../../_lib/detail/show-detail-types';
import { getDefaultSelectedDate } from '../../_lib/detail/formatters';
import { ShowDetailHeader } from './ShowDetailHeader';
import { ShowDetailsCard } from './ShowDetailsCard';
import { ShowDetailTabs } from './ShowDetailTabs';
import { TicketBookingPanel } from './TicketBookingPanel';
import { MobileFindTicketsBar } from './MobileFindTicketsBar';
import { useMobileCalendarSheet } from '../../_lib/useMobileCalendarSheet';
import { Nearby } from '../../../_components/nearby/Nearby';
import { BACK_FORWARD_RELOAD_SCRIPT } from '../../_lib/back-forward-reload-script';

interface ShowDetailClientProps {
  show: ShowDetailRecord;
  todayKey: string;
}

export function ShowDetailClient({ show, todayKey }: ShowDetailClientProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Read initial date from URL query param `?bookingDate=...` or default to the show's first upcoming performance date, or today
  const initialDate =
    searchParams?.get('bookingDate') ||
    getDefaultSelectedDate(show.performances as any, todayKey) ||
    (show.event.startDate && show.event.startDate.slice(0, 10) >= todayKey
      ? show.event.startDate.slice(0, 10)
      : todayKey);

  const [selectedDate, setSelectedDate] = useState<string>(initialDate);

  // Priority: 1) show-scoped sessionStorage (latest selection across detail & seat selection pages),
  // 2) URL search param `?quantity=...`, 3) default 1.
  const [ticketQuantity, setTicketQuantity] = useState<number>(() => {
    if (typeof window !== 'undefined') {
      try {
        const stored = sessionStorage.getItem(`bip_show_tickets_${show.showId}`);
        if (stored && Number(stored) > 0) {
          return Number(stored);
        }
      } catch {}
    }
    const fromUrl = Number(searchParams?.get('quantity') || searchParams?.get('tickets'));
    return fromUrl > 0 ? fromUrl : 1;
  });

  // Sync latest quantity from show-scoped storage on mount, back/forward cache restore (pageshow), and window focus
  useEffect(() => {
    function syncTicketQuantity() {
      try {
        // Clean up legacy global key if present
        sessionStorage.removeItem('bip_show_tickets');
        localStorage.removeItem('bip_show_tickets');

        const stored = sessionStorage.getItem(`bip_show_tickets_${show.showId}`);
        if (stored && Number(stored) > 0) {
          const num = Number(stored);
          setTicketQuantity((prev) => (prev !== num ? num : prev));
          const params = new URLSearchParams(window.location.search);
          if (params.get('quantity') !== stored) {
            params.set('quantity', stored);
            window.history.replaceState(null, '', `${pathname}?${params.toString()}`);
          }
        }
      } catch {}
    }

    syncTicketQuantity();
    window.addEventListener('pageshow', syncTicketQuantity);
    window.addEventListener('focus', syncTicketQuantity);
    return () => {
      window.removeEventListener('pageshow', syncTicketQuantity);
      window.removeEventListener('focus', syncTicketQuantity);
    };
  }, [show.showId, pathname]);

  // `LtdCalendarWidget` takes an ISO `defaultDate` so it opens on whatever
  // date the user arrived with (from `?bookingDate=` or the show's own
  // startDate), instead of always defaulting to today — same pattern as
  // the Seat Selection Page's `defaultDateForWidget`.
  const defaultDateForWidget = selectedDate ? new Date(`${selectedDate}T00:00:00Z`).toISOString() : undefined;

  // Sync with searchParams if bookingDate changes from outside (e.g. back/forward button)
  useEffect(() => {
    const paramDate = searchParams?.get('bookingDate');
    if (paramDate && paramDate !== selectedDate) {
      setSelectedDate(paramDate);
    }
  }, [searchParams, selectedDate]);

  const handleDateSelect = useCallback(
    (newDate: string) => {
      if (!newDate) return;
      setSelectedDate(newDate);

      // Update browser URL query parameter dynamically without full page reload
      try {
        const params = new URLSearchParams(window.location.search);
        params.set('bookingDate', newDate);
        const newUrl = `${pathname}?${params.toString()}`;
        window.history.replaceState(null, '', newUrl);
      } catch {
        // Fallback for environments without history API
      }
    },
    [pathname],
  );

  const handleQuantityChange = useCallback(
    (newQty: number) => {
      if (!newQty || newQty < 1) return;
      setTicketQuantity(newQty);
      try {
        sessionStorage.setItem(`bip_show_tickets_${show.showId}`, String(newQty));
        const params = new URLSearchParams(window.location.search);
        params.set('quantity', String(newQty));
        window.history.replaceState(null, '', `${pathname}?${params.toString()}`);
      } catch {}
    },
    [show.showId, pathname],
  );

  // Check if show is completely sold out or has bookable performances
  const isSoldOut = useMemo(() => {
    return (
      !show.performances ||
      show.performances.length === 0 ||
      show.performances.every(
        (p) => !p.isBookable || (p.availableTickets != null && p.availableTickets <= 0),
      )
    );
  }, [show.performances]);

  // Minimum price for the selected date, upcoming from selected date, or overall minimum price
  const minPrice = useMemo(() => {
    const bookable = (show.performances || []).filter(
      (p) => p.isBookable && (p.availableTickets == null || p.availableTickets > 0) && p.minPrice > 0,
    );
    if (bookable.length === 0) return null;
    if (selectedDate) {
      const onDate = bookable.filter((p) => p.dateTime.startsWith(selectedDate));
      if (onDate.length > 0) {
        return Math.min(...onDate.map((p) => p.minPrice));
      }
      const afterDate = bookable.filter((p) => p.dateTime.slice(0, 10) >= selectedDate);
      if (afterDate.length > 0) {
        return Math.min(...afterDate.map((p) => p.minPrice));
      }
    }
    return Math.min(...bookable.map((p) => p.minPrice));
  }, [show.performances, selectedDate]);

  // Mobile calendar sheet — below 768px the whole booking sidebar is collapsed
  // out of the layout and only opens from the sticky "Find Tickets" bar (and the
  // header's "Buy Tickets"). Above it, `openSheet` degrades to the previous
  // scroll-the-panel-into-view behaviour. See `useMobileCalendarSheet`.
  const {
    open: mobileCalendarOpen,
    openSheet: handleOpenMobileWidget,
    sheetProps,
  } = useMobileCalendarSheet('.shows-detail-sidebar');

  return (
    <div className="shows-detail-page" {...sheetProps}>
      <script dangerouslySetInnerHTML={{ __html: BACK_FORWARD_RELOAD_SCRIPT }} />
      {/* FR-HEAD-01..09 */}
      <ShowDetailHeader show={show} onBuyTickets={handleOpenMobileWidget} />

      <div className="shows-container shows-detail-main-grid">
        <div className="shows-detail-main-content">
          {/* FR-DET-01..09 */}
          <ShowDetailsCard show={show} />
          {/* FR-TAB-01..07 */}
          <ShowDetailTabs show={show} todayKey={todayKey} />
        </div>
        <aside className="shows-detail-sidebar">
          {/* FR-CAL-01..13 */}
          <TicketBookingPanel
            show={show}
            defaultDate={defaultDateForWidget}
            initialQuantity={ticketQuantity}
            onQuantityChange={handleQuantityChange}
            onDateSelect={handleDateSelect}
            // While the vendor's mobile overlay is up, the stepper is portalled
            // INTO that overlay — the inline one has nothing to sit above on
            // mobile. See `LtdCalendarWidget`'s `forceTicketHeader`.
            forceTicketHeader={mobileCalendarOpen}
          />
        </aside>
      </div>

      {/* FR-HOTEL-01..05 — real cross-sell via the shared `<Nearby>`
          framework. Excludes only `shows` (a page never cross-sells its own
          domain) so Hotels / Sports / Restaurants / Concerts all render from
          the SAME adapters + cards the sports, concerts and restaurants
          detail pages use — no shows-specific nearby logic exists. Every
          strip is scoped to the selected calendar date via `checkin` /
          `dateFrom` (already ISO `YYYY-MM-DD` here, which is what every
          adapter expects). `limit` is deliberately NOT set: the adapters'
          own default (10 fetched, first 4 rendered) is what gates each
          strip's Explore button, and capping the fetch at 4 would suppress
          it. */}
      <div className="shows-container">
        <Nearby
          key={`nearby-${selectedDate}`}
          excludeCategory="shows"
          referenceName={show.venue.theatreName}
          latitude={show.venue.latitude || undefined}
          longitude={show.venue.longitude || undefined}
          cityName={show.venue.city}
          dateFrom={selectedDate}
          checkin={selectedDate}
        />
      </div>

      {/* Sticky mobile "Find Tickets" bar matching theater CMS reference code */}
      <MobileFindTicketsBar
        minPrice={minPrice}
        currency={show.event.currency}
        isSoldOut={isSoldOut}
        onFindTickets={handleOpenMobileWidget}
      />
    </div>
  );
}
